package br.com.vvrefeicoes.portal.feature.rh

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.domain.model.RhCountItem
import br.com.vvrefeicoes.portal.feature.operacional.ChartCard
import br.com.vvrefeicoes.portal.feature.operacional.HoverTooltip
import br.com.vvrefeicoes.portal.feature.operacional.LegendEntry
import br.com.vvrefeicoes.portal.feature.operacional.LinePointLabel
import br.com.vvrefeicoes.portal.feature.operacional.SeriesLegend
import br.com.vvrefeicoes.portal.feature.operacional.rememberValueAxis
import io.github.koalaplot.core.bar.DefaultBar
import io.github.koalaplot.core.bar.DefaultBarPlotGroupedPointEntry
import io.github.koalaplot.core.bar.DefaultBarPosition
import io.github.koalaplot.core.bar.GroupedVerticalBarPlot
import io.github.koalaplot.core.line.LinePlot
import io.github.koalaplot.core.style.LineStyle
import io.github.koalaplot.core.xygraph.CategoryAxisModel
import io.github.koalaplot.core.xygraph.Point
import io.github.koalaplot.core.xygraph.XYGraph
import io.github.koalaplot.core.xygraph.rememberAxisContent
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rh_panel_no_data
import portalvv.shared.generated.resources.rmp_retry
import kotlin.math.abs
import kotlin.math.round
import kotlin.math.roundToLong

private val KPI_HEIGHT = 96.dp
private val CHART_HEIGHT = 240.dp
private val DISTRIBUTION_BAR_HEIGHT = 6.dp
private const val AXIS_HEADROOM = 1.15f

internal const val RH_EMPTY_VALUE = "—"

internal fun rhMoney(value: Double): String = "R$ ${MoneyFormatter.formatCents(round(value * 100).toLong())}"

/** Valor monetário abreviado, para rótulos de eixo e de ponto dos gráficos. */
internal fun rhMoneyCompact(value: Double): String {
    val absolute = abs(value)
    return when {
        absolute >= 1_000_000 -> "R$ ${rhDecimal(value / 1_000_000, 1)} mi"
        absolute >= 1_000 -> "R$ ${rhDecimal(value / 1_000, 0)} mil"
        else -> "R$ ${rhDecimal(value, 0)}"
    }
}

internal fun rhPercent(value: Double): String = "${rhDecimal(value, 2)}%"

internal fun rhDecimal(
    value: Double,
    decimals: Int,
): String {
    var factor = 1L
    repeat(decimals) { factor *= 10 }
    val scaled = (abs(value) * factor).roundToLong()
    val sign = if (value < 0 && scaled != 0L) "-" else ""
    val integer = scaled / factor
    if (decimals == 0) return "$sign$integer"
    return "$sign$integer,${(scaled % factor).toString().padStart(decimals, '0')}"
}

internal fun String?.orRhDash(): String = if (isNullOrBlank()) RH_EMPTY_VALUE else this

/** Distribui [items] em linhas com o número de colunas que couber na largura disponível. */
@Composable
internal fun RhGrid(
    items: List<@Composable (Modifier) -> Unit>,
    minColumnWidth: Dp,
    maxColumns: Int,
    modifier: Modifier = Modifier,
) {
    BoxWithConstraints(modifier = modifier.fillMaxWidth()) {
        val columns = (maxWidth / minColumnWidth).toInt().coerceIn(1, maxColumns)
        Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
            items.chunked(columns).forEach { rowItems ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
                    verticalAlignment = Alignment.Top,
                ) {
                    rowItems.forEach { item -> item(Modifier.weight(1f)) }
                    repeat(columns - rowItems.size) { Spacer(Modifier.weight(1f)) }
                }
            }
        }
    }
}

@Composable
internal fun RhKpiTile(
    icon: ImageVector,
    iconColor: Color,
    iconContainerColor: Color,
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    hint: String? = null,
) {
    Surface(
        modifier = modifier.height(KPI_HEIGHT),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space12),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Surface(
                shape = AppShapes.small,
                color = iconContainerColor,
                modifier = Modifier.size(32.dp),
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        imageVector = icon,
                        contentDescription = null,
                        tint = iconColor,
                        modifier = Modifier.size(16.dp),
                    )
                }
            }
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                Text(
                    text = label,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = value,
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.onSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                if (hint != null) {
                    Text(
                        text = hint,
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            }
        }
    }
}

internal data class RhChartSeries(
    val label: String,
    val color: Color,
    val values: List<Float>,
)

@Composable
internal fun RhGroupedBarCard(
    title: String,
    labels: List<String>,
    series: List<RhChartSeries>,
    format: (Float) -> String,
    modifier: Modifier = Modifier,
) {
    ChartCard(title = title, modifier = modifier) {
        SeriesLegend(entries = series.map { LegendEntry(it.color, it.label) })
        if (labels.isEmpty() || series.isEmpty()) {
            RhEmptyChart()
        } else {
            val grouped =
                labels.mapIndexed { index, label ->
                    DefaultBarPlotGroupedPointEntry(
                        i = label,
                        d = series.map { DefaultBarPosition(0f, it.values.getOrElse(index) { 0f }) },
                    )
                }
            val maxValue = series.flatMap { it.values }.maxOrNull()?.coerceAtLeast(1f) ?: 1f
            XYGraph(
                gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
                xAxisModel = CategoryAxisModel(labels),
                yAxisModel = rememberValueAxis(maxValue),
                xAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
                yAxisContent = rememberAxisContent(labels = { value -> Text(format(value), style = MaterialTheme.typography.labelSmall) }),
                modifier = Modifier.fillMaxWidth().height(CHART_HEIGHT),
            ) {
                GroupedVerticalBarPlot(
                    data = grouped,
                    maxBarGroupWidth = 0.6f,
                    bar = { dataIndex, seriesIndex, value ->
                        val tooltip =
                            buildString {
                                append(value.i)
                                series.forEach { item ->
                                    append('\n')
                                    append(item.label)
                                    append(": ")
                                    append(format(item.values.getOrElse(dataIndex) { 0f }))
                                }
                            }
                        HoverTooltip(text = tooltip) {
                            DefaultBar(color = series.getOrNull(seriesIndex)?.color ?: MaterialTheme.colorScheme.primary)
                        }
                    },
                )
            }
        }
    }
}

/** Linha de evolução mensal. Meses sem valor (`null`) ficam fora da linha. */
@Composable
internal fun RhLineCard(
    title: String,
    legend: String,
    labels: List<String>,
    values: List<Float?>,
    color: Color,
    format: (Float) -> String,
    modifier: Modifier = Modifier,
) {
    ChartCard(title = title, modifier = modifier) {
        SeriesLegend(entries = listOf(LegendEntry(color, legend)))
        val points = labels.mapIndexedNotNull { index, label -> values.getOrNull(index)?.let { Point(label, it) } }
        if (points.isEmpty()) {
            RhEmptyChart()
        } else {
            val maxValue = points.maxOf { it.y }.coerceAtLeast(1f)
            XYGraph(
                gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
                xAxisModel = CategoryAxisModel(labels),
                yAxisModel = rememberValueAxis(maxValue, headroom = AXIS_HEADROOM),
                xAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
                yAxisContent = rememberAxisContent(labels = { value -> Text(format(value), style = MaterialTheme.typography.labelSmall) }),
                modifier = Modifier.fillMaxWidth().height(CHART_HEIGHT),
            ) {
                LinePlot(
                    data = points,
                    lineStyle = LineStyle(brush = SolidColor(color), strokeWidth = 2.dp),
                    symbol = { point ->
                        HoverTooltip(text = "${point.x}: ${format(point.y)}") {
                            LinePointLabel(format(point.y), color)
                        }
                    },
                )
            }
        }
    }
}

/** Lista de contagens com uma barra proporcional ao maior item. */
@Composable
internal fun RhDistributionCard(
    title: String,
    items: List<RhCountItem>,
    color: Color,
    modifier: Modifier = Modifier,
) {
    ChartCard(title = title, modifier = modifier) {
        if (items.isEmpty()) {
            RhEmptyChart()
        } else {
            val maxCount = items.maxOf { it.count }.coerceAtLeast(1)
            items.forEach { item ->
                Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = item.label,
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurface,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.weight(1f),
                        )
                        Text(
                            text = item.count.toString(),
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.SemiBold,
                            color = MaterialTheme.colorScheme.onSurface,
                        )
                    }
                    Box(
                        modifier =
                            Modifier
                                .fillMaxWidth()
                                .height(DISTRIBUTION_BAR_HEIGHT)
                                .background(MaterialTheme.colorScheme.surfaceVariant, AppShapes.small),
                    ) {
                        Box(
                            modifier =
                                Modifier
                                    .fillMaxWidth(item.count.toFloat() / maxCount)
                                    .height(DISTRIBUTION_BAR_HEIGHT)
                                    .background(color, AppShapes.small),
                        )
                    }
                }
            }
        }
    }
}

@Composable
internal fun RhEmptyChart() {
    Text(
        text = stringResource(Res.string.rh_panel_no_data),
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )
}

@Composable
internal fun RhLoading() {
    Box(
        modifier = Modifier.fillMaxWidth().height(200.dp),
        contentAlignment = Alignment.Center,
    ) {
        CircularProgressIndicator()
    }
}

@Composable
internal fun RhLoadError(
    message: String,
    onRetry: () -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space24),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
    ) {
        Text(
            text = message,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.error,
        )
        AppButton(
            text = stringResource(Res.string.rmp_retry),
            onClick = onRetry,
            variant = PortalButtonVariant.Outlined,
        )
    }
}
